import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useUser } from '@auth0/nextjs-auth0/client';
import PropTypes from 'prop-types';

import { loadState, saveState } from 'utils/localStorage';

const LeadContext = createContext();

LeadContext.propTypes = {
  children: PropTypes.node.isRequired,
  shouldScheduleMeeting: PropTypes.bool,
  countries: PropTypes.array,
  schoolsFilters: PropTypes.object,
};

export function LeadProvider({
  children,
  shouldScheduleMeeting = false,
  countries = [],
  schoolsFilters = {},
}) {
  const { user } = useUser();
  const [lead, setLead] = useState(() => {
    if (typeof window !== 'undefined') {
      const localStorageLead = loadState()?.lead;

      if (localStorageLead) {
        return localStorageLead;
      }
    }

    return {};
  });
  const [step, setStep] = useState(1);
  const [isPremiumPlanUser, setIsPremiumPlanUser] = useState(false);
  const leadFirstName = lead?.firstname || lead?.firstName || '';
  const [openLeadForm, setOpenLeadForm] = useState(false);
  const firstLoad = useRef(true);

  const handleOpenLeadForm = useCallback(() => {
    setOpenLeadForm(true);
  }, []);

  const handleCloseLeadForm = useCallback(() => {
    setOpenLeadForm(false);
  }, []);

  const addLead = useCallback((newLead) => {
    setLead(newLead);
    saveState({ lead: newLead });
  }, []);

  const updateLead = useCallback(
    (updatedLead) => {
      const newLead = { ...lead, ...updatedLead };

      addLead(newLead);

      return newLead;
    },
    [addLead, lead]
  );

  const setPriceOpen = useCallback(() => {
    const newLead = updateLead({ priceOpen: true });

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('updateLead', { detail: newLead }));
    }
  }, [updateLead]);

  const getLeadFromSessionStorage = useCallback(() => {
    if (Object.keys(lead).length > 0) {
      return lead;
    }

    return loadState()?.lead;
  }, [lead]);

  useEffect(() => {
    if (!firstLoad.current) {
      return;
    }

    if (shouldScheduleMeeting) {
      setStep(3);
      return;
    }

    if (lead?.step) {
      setStep(lead?.step);
    }

    firstLoad.current = false;
  }, [lead?.step, shouldScheduleMeeting, user]);

  useEffect(() => {
    const getNewLead = (event) => {
      setLead(event.detail);
    };

    window.addEventListener('updateLead', getNewLead);

    return () => {
      window.removeEventListener('updateLead', getNewLead);
    };
  }, [openLeadForm]);

  const values = useMemo(
    () => ({
      addLead,
      countries,
      getLeadFromSessionStorage,
      handleCloseLeadForm,
      handleOpenLeadForm,
      isPremiumPlanUser,
      lead,
      leadFirstName,
      openLeadForm,
      schoolsFilters,
      setIsPremiumPlanUser,
      setPriceOpen,
      setStep,
      step,
      updateLead,
    }),
    [
      addLead,
      countries,
      getLeadFromSessionStorage,
      handleCloseLeadForm,
      handleOpenLeadForm,
      isPremiumPlanUser,
      lead,
      leadFirstName,
      openLeadForm,
      schoolsFilters,
      setPriceOpen,
      step,
      updateLead,
    ]
  );
  return <LeadContext.Provider value={values}>{children}</LeadContext.Provider>;
}

export const useLead = () => {
  const context = useContext(LeadContext);

  if (!context) {
    throw new Error('useLead must be used within a LeadProvider');
  }

  return context;
};
